import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Chip, 
  Grid,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import { 
  Tag as TagIcon, 
  Label, 
  Refresh as RefreshIcon 
} from '@mui/icons-material';
import { api } from "../services/api";
import PropTypes from 'prop-types';

const TagTab = ({ resource }) => {
  const [resourceDetails, setResourceDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Helper function to assign colors based on tag type and subTab
  const getTagColor = (tagKey, subTab) => {
    const colorMap = {
      // SubTab-based colors
      'General': '#2196f3',        // Blue
      'Optional': '#ff9800',       // Orange  
      'Logic Monitor': '#9c27b0',  // Purple
      
      // Tag key-based colors
      'environment': '#4caf50',    // Green
      'project': '#2196f3',        // Blue
      'company': '#795548',        // Brown
      'email': '#f44336',          // Red
      'account': '#607d8b',        // Blue Grey
      'name': '#00bcd4',           // Cyan
      'customer': '#e91e63',       // Pink
      'default': '#1976d2'         // Default blue
    };
    
    // First try to get color by subTab
    if (subTab && colorMap[subTab]) {
      return colorMap[subTab];
    }
    
    // Then try by tag key
    if (tagKey) {
      const lowerKey = tagKey.toLowerCase();
      for (const [key, color] of Object.entries(colorMap)) {
        if (lowerKey.includes(key.toLowerCase())) {
          return color;
        }
      }
    }
    
    return colorMap.default;
  };

  // Extract tags from API response - Updated for your structure
  const extractTagsFromResponse = () => {
    const tags = [];
    
    if (resourceDetails && Array.isArray(resourceDetails)) {
      // Filter items where tabName is "Tag"
      const tagItems = resourceDetails.filter(detail => 
        detail && detail.tabName && detail.tabName.toLowerCase() === 'tag'
      );
      
      tagItems.forEach(detail => {
        if (detail.fieldName && detail.fieldData !== null && detail.fieldData !== undefined) {
          tags.push({
            key: detail.fieldName,
            value: detail.fieldData,
            subTab: detail.subTab || 'General',
            color: getTagColor(detail.fieldName, detail.subTab),
            createdBy: 'CMP',
            createdAt: new Date().toISOString(),
            requestId: detail.requestId
          });
        }
      });
    }

    return tags;
  };

  // Group tags by subTab for better organization
  const groupTagsBySubTab = (tags) => {
    const grouped = {};
    tags.forEach(tag => {
      const subTab = tag.subTab || 'General';
      if (!grouped[subTab]) {
        grouped[subTab] = [];
      }
      grouped[subTab].push(tag);
    });
    return grouped;
  };

  const fetchDetails = useCallback(async () => {
    if (!resource || !resource.resourceId) {
      setError('No resource ID provided');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(
        `/api/Resources/viewResourceRequest/${resource.resourceId}`,
        {
          headers: {
            "X-CORRELATION-ID": "0d89a8fe-6a3f-46e6-8bde-72daf869b07c",
          },
        }
      );
      
      
      if (response && response.data) {
        setResourceDetails(response.data.resourceRequestDetails || []);
      } else {
        setError('Invalid response from server');
      }
    } catch (error) {
      console.error("Error fetching resource details:", error);
      setError('Failed to fetch resource details');
    } finally {
      setLoading(false);
    }
  }, [resource]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDetails();
    setRefreshing(false);
  };

  useEffect(() => {
    if (resource && resource.resourceId) {
      fetchDetails();
    }
  }, [resource, fetchDetails]);

  // Convert extracted tags to array format
  const tagArray = extractTagsFromResponse();
  const groupedTags = groupTagsBySubTab(tagArray);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography>Loading tags...</Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ py: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={handleRefresh}
          startIcon={<RefreshIcon />}
        >
          Retry
        </Button>
      </Box>
    );
  }

  // No resource provided
  if (!resource) {
    return (
      <Box sx={{ py: 3 }}>
        <Alert severity="warning">
          Please select a resource to view tags
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      {/* Header with Actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Resource Tags</Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh tags">
            <IconButton 
              onClick={handleRefresh} 
              disabled={refreshing}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          {/* <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddTagDialog(true)}
            size="small"
          >
            Add Tag
          </Button>
           */}
        </Box>
      </Box>

            {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Total Tags</Typography>
                <TagIcon color="primary" />
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {tagArray ? tagArray.length : 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Resource tags configured
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Categories</Typography>
                <Label color="success" />
              </Box>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {Object.keys(groupedTags).length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tag categories
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">General Tags</Typography>
                <TagIcon sx={{ color: '#2196f3' }} />
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: '#2196f3' }}>
                {groupedTags['General'] ? groupedTags['General'].length : 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                General category tags
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Optional Tags</Typography>
                <TagIcon sx={{ color: '#ff9800' }} />
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: '#ff9800' }}>
                {groupedTags['Optional'] ? groupedTags['Optional'].length : 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Optional category tags
              </Typography>
            </CardContent>
          </Card>
        </Grid>

          <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Logic Monitor Tags</Typography>
                <TagIcon sx={{ color: "success.main" }} />
              </Box>
              <Typography variant="h4" fontWeight="bold" color="success.main" >
                {groupedTags['Logic Monitor'] ? groupedTags['Logic Monitor'].length : 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
               Logic Monitor tags
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tags by Category */}
      {Object.keys(groupedTags).length > 0 ? (
        Object.entries(groupedTags).map(([subTab, tags]) => (
          <Card key={subTab} sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  {subTab || 'General'} Tags ({tags.length})
                </Typography>
                <Chip 
                  label={subTab || 'General'} 
                  size="small"
                  sx={{ 
                    backgroundColor: getTagColor('', subTab),
                    color: 'white'
                  }}
                />
              </Box>
              
              <Box display="flex" flexWrap="wrap" gap={1}>
                {tags.map((tag, index) => (
                  <Chip
                    key={`${tag.key}-${index}`}
                    label={`${tag.key}: ${tag.value}`}
                    // onDelete={() => handleDeleteTag(tag.key)}
                    // deleteIcon={<DeleteIcon />}
                    sx={{ 
                      backgroundColor: tag.color,
                      color: 'white',
                      '& .MuiChip-deleteIcon': {
                        color: 'white',
                        '&:hover': {
                          color: '#ffcccb'
                        }
                      }
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        ))
      ) : (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box 
              display="flex" 
              flexDirection="column"
              justifyContent="center" 
              alignItems="center" 
              minHeight="200px"
              sx={{ 
                border: '2px dashed #e0e0e0', 
                borderRadius: 2,
                backgroundColor: '#fafafa'
              }}
            >
              <TagIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" mb={1}>
                No tags found
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                This resource doesn't have any tags configured
              </Typography>
              {/* <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddTagDialog(true)}
              >
                Add First Tag
              </Button> */}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Detailed Tag Information
      {tagArray && tagArray.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={3}>Tag Details</Typography>
            <Grid container spacing={2}>
              {tagArray.map((tag, index) => (
                <Grid item xs={12} sm={6} md={4} key={`${tag.key}-detail-${index}`}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box flex={1}>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>
                            {tag.key}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                            {tag.value}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="Edit tag">
                            <IconButton size="small" color="primary">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete tag">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteTag(tag.key)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      
                      <Divider sx={{ my: 1 }} />
                      
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box 
                            sx={{ 
                              width: 12, 
                              height: 12, 
                              borderRadius: '50%', 
                              backgroundColor: tag.color 
                            }} 
                          />
                          <Typography variant="caption" color="text.secondary">
                            {tag.subTab || 'General'}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          ID: {tag.requestId}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                          ))}
            </Grid>
          </CardContent>
        </Card>
      )} */}

      {/* Add Tag Dialog */}
      {/* <Dialog 
        open={addTagDialog} 
        onClose={() => setAddTagDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Tag</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Tag Key"
              fullWidth
              variant="outlined"
              value={newTag.key}
              onChange={(e) => setNewTag({ ...newTag, key: e.target.value })}
              placeholder="e.g., environment, project, owner"
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Tag Value"
              fullWidth
              variant="outlined"
              value={newTag.value}
              onChange={(e) => setNewTag({ ...newTag, value: e.target.value })}
              placeholder="e.g., production, web-app, john.doe"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTagDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddTag}
            variant="contained"
            disabled={!newTag.key || !newTag.value}
          >
            Add Tag
          </Button>
        </DialogActions>
      </Dialog> */}

      {/* Resource Information Summary */}
      {/* <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={2}>Resource Information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Resource ID</Typography>
              <Typography variant="body1" fontWeight="bold">
                {viewResponse.resource_Id || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Application</Typography>
              <Typography variant="body1" fontWeight="bold">
                {viewResponse.applicationName || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Private IP</Typography>
              <Typography variant="body1" fontWeight="bold">
                {viewResponse.privateIP || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Total Price</Typography>
              <Typography variant="body1" fontWeight="bold" color="success.main">
                ${viewResponse.totalPrice || '0.00'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card> */}

      {/* Debug Information (Development only) */}
      {/* {process.env.NODE_ENV === 'development' && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" mb={2}>Debug Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" mb={1}>
                  <strong>Resource ID:</strong> {resource?.resourceId || 'Not provided'}
                </Typography>
                <Typography variant="body2" mb={1}>
                  <strong>Resource Details Count:</strong> {resourceDetails?.length || 0}
                </Typography>
                <Typography variant="body2" mb={1}>
                  <strong>Tag Items Found:</strong> {
                    resourceDetails?.filter(d => d.tabName?.toLowerCase() === 'tag').length || 0
                  }
                </Typography>
                <Typography variant="body2" mb={1}>
                  <strong>Grouped Categories:</strong> {Object.keys(groupedTags).join(', ') || 'None'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" mb={1}>
                  <strong>Sample Tag Data:</strong>
                </Typography>
                <Box 
                  component="pre" 
                  sx={{ 
                    fontSize: '0.75rem', 
                    backgroundColor: '#f5f5f5', 
                    p: 1, 
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: 200
                  }}
                >
                  {JSON.stringify(tagArray.slice(0, 3), null, 2)}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )} */}
    </Box>
  );
};
TagTab.propTypes = {
  resource: PropTypes.shape({
    resourceId: PropTypes.string.isRequired,
  }),
};
TagTab.defaultProps = {
  resource: null
};

export default TagTab;